import {Component, DestroyRef, OnInit, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DrawerModule} from "primeng/drawer";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {ButtonModule} from "primeng/button";
import {NgClass, NgForOf, NgIf, NgTemplateOutlet} from "@angular/common";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {RoleEnum} from "../../core/modules/enums/roleEnum";
import {SUPER_ADMIN_PERMISSION} from "../../core/modules/admin-permissions";
import {AuthService} from "../../core/service/auth/Auth.service";
import {TranslatePipe} from "@ngx-translate/core";
import {LayoutService} from "../layout.service";
import {LocaleService} from "../../core/i18n/locale.service";
import {ADMIN_INVITATIONS_ACCESS} from "../../core/modules/admin-invitations/admin-invitation.access";
import {INVOICES_ACCESS} from "../../core/modules/invoices/invoice.access";
import {BANK_OPS_ACCESS} from "../../core/modules/bank-operations/bank-ops.access";
import {AI_ASSISTANT_ACCESS, AI_COSTS_ACCESS, AI_MODELS_ACCESS} from "../../core/modules/ai-assistant/ai-admin.access";
import {WATHQ_COSTS_ACCESS} from "../../core/modules/wathq/wathq-costs.models";
import {BULK_IMPORT_JOBS_ACCESS, BULK_IMPORT_SETTINGS_ACCESS} from "../../core/modules/product-imports/bulk-import.access";
import {PAYMENT_SETTINGS_ACCESS} from "../../core/modules/payment-settings/payment-settings.models";

export interface SideMenuItem {
  label: string;
  icon: string;
  roles?: RoleEnum[];
  permission?: string;
  /** Shown when the caller holds any one of these. */
  anyPermission?: readonly string[];
  permissionGroup?: string;
  link?: string;
  queryParams?: Record<string, string>;
  comingSoon?: boolean;
}

/**
 * One titled group of links. The menu is a single level: a section is the only thing that
 * folds, and its items are plain links (no nested submenus). A section without a title is
 * always shown expanded. Every item carries its own full gating — nothing is inherited from
 * the section — and a section whose items the caller can see none of is hidden entirely.
 */
export interface SideMenuSection {
  key: string;
  title?: string;
  items: SideMenuItem[];
}

/**
 * Titled sections start folded and only open when the user opens them, so what is stored
 * is the set of sections opened. (The old key held the folded set and is no longer read.)
 */
const EXPANDED_SECTIONS_STORAGE_KEY = 'sidenav.expandedSections';


@Component({
  selector: 'app-sidenav',
  standalone: true,
  imports: [
    DrawerModule,
    RouterLink,
    ButtonModule,
    NgForOf,
    NgIf,
    NgClass,
    NgTemplateOutlet,
    TranslatePipe,
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.css',
  animations: [
    trigger('submenuToggle', [
      state('closed', style({height: '0px', overflow: 'hidden', opacity: 0})),
      state('open', style({height: '*', opacity: 1})),
      transition('closed <=> open', animate('300ms ease-in-out'))
    ])
  ]
})
export class SidenavComponent implements OnInit {
  private readonly layoutService = inject(LayoutService);
  private readonly localeService = inject(LocaleService);
  private readonly destroyRef = inject(DestroyRef);

  /** Mobile drawer visibility, driven by the nav bar's hamburger via LayoutService. */
  drawerVisible = false;

  /** Drawer slides in from the reading-start side: left in LTR, right in RTL. */
  get drawerPosition(): 'left' | 'right' {
    return this.localeService.current === 'ar' ? 'right' : 'left';
  }

  sections: SideMenuSection[] = [
    {
      key: 'overview',
      items: [
        {label: 'sidenav.home',          icon: 'pi pi-home',      link: '/home',           roles: [RoleEnum.Admin]},
        {label: 'sidenav.dashboard',     icon: 'pi pi-chart-bar', link: '/dashboard',      roles: [RoleEnum.Admin], permissionGroup: 'dashboard'},
        {label: 'sidenav.commandCenter', icon: 'pi pi-bolt',      link: '/command-center', roles: [RoleEnum.Admin], permissionGroup: 'dashboard'},
        {label: 'sidenav.salesCustomerServices', icon: 'pi pi-headphones', link: '/sales/customer-services', roles: [RoleEnum.Admin, RoleEnum.SalesManager]},
      ]
    },
    {
      key: 'members', title: 'sidenav.sections.members',
      items: [
        {label: 'sidenav.allUsers',           icon: 'pi pi-users',   link: '/users',     roles: [RoleEnum.Admin], permissionGroup: 'users'},
        {label: 'sidenav.buyers',             icon: 'pi pi-id-card', link: '/buyers',    roles: [RoleEnum.Admin], permissionGroup: 'buyers'},
        {label: 'sidenav.suppliers',          icon: 'pi pi-id-card', link: '/suppliers', roles: [RoleEnum.Admin], permissionGroup: 'suppliers'},
        // Invitations sent to suppliers, restaurants and hotels. Not the landing codes under Settings, and not
        // the Salesman-only link screen under Sales. Spreads the same constant as the route, so what a
        // SubAdmin sees here is exactly what they can open.
        {label: 'sidenav.adminInvitations',   icon: 'pi pi-send',    link: '/admin/invitations', ...ADMIN_INVITATIONS_ACCESS},
        // Who introduced whom, and every use of a public link. Same access constant, so it
        // appears for exactly the people who can open Invitations itself.
        {label: 'sidenav.adminInvitationAudit', icon: 'pi pi-sitemap', link: '/admin/invitations/audit', ...ADMIN_INVITATIONS_ACCESS},
        {label: 'sidenav.adminTeam',         icon: 'pi pi-shield',  link: '/admin/team',    roles: [RoleEnum.Admin], permission: SUPER_ADMIN_PERMISSION},
      ]
    },
    {
      key: 'orders', title: 'sidenav.sections.orders',
      items: [
        {label: 'sidenav.orders',       icon: 'pi pi-box',    link: '/orders',        roles: [RoleEnum.Admin], permissionGroup: 'orders'},
        {label: 'sidenav.requestOrder', icon: 'pi pi-globe',  link: '/request-order', roles: [RoleEnum.Admin], permissionGroup: 'requestorders'},
        {label: 'sidenav.returns',      icon: 'pi pi-replay', link: '/returns',       roles: [RoleEnum.Admin], permissionGroup: 'returns'},
        {label: 'sidenav.complaints',   icon: 'pi pi-flag',   link: '/complaints',    roles: [RoleEnum.Admin], permissionGroup: 'complaints'},
      ]
    },
    {
      key: 'catalog', title: 'sidenav.sections.catalog',
      items: [
        {label: 'sidenav.products',          icon: 'pi pi-inbox',      link: '/products',                        roles: [RoleEnum.Admin], permissionGroup: 'products'},
        // Buyers' ratings of products (each row names the product and its supplier).
        {label: 'sidenav.reviews',           icon: 'pi pi-star',       link: '/reviews',                         roles: [RoleEnum.Admin], permissionGroup: 'reviews'},
        {label: 'sidenav.categories',        icon: 'pi pi-folder',     link: '/categories',                      roles: [RoleEnum.Admin]},
        {label: 'sidenav.cities',            icon: 'pi pi-building',   link: '/project/cities',                  roles: [RoleEnum.Admin], permissionGroup: 'project'},
        {label: 'sidenav.cuisines',          icon: 'pi pi-shop',       link: '/project/cuisines',                roles: [RoleEnum.Admin], permissionGroup: 'project'},
        {label: 'sidenav.comSupplies',       icon: 'pi pi-shop',       link: '/project/common-supplies',         roles: [RoleEnum.Admin], permissionGroup: 'project'},
        {label: 'sidenav.specSupplies',      icon: 'pi pi-shop',       link: '/project/supplies-specialization', roles: [RoleEnum.Admin], permissionGroup: 'project'},
        {label: 'sidenav.supplierTypes',     icon: 'pi pi-briefcase',  link: '/project/supplier-types',          roles: [RoleEnum.Admin], permissionGroup: 'project'},
        {label: 'sidenav.deficitReasons',    icon: 'pi pi-list-check', link: '/project/deficit-reasons',         roles: [RoleEnum.Admin], permissionGroup: 'project'},
        {label: 'sidenav.platformDocuments', icon: 'pi pi-file-pdf',   link: '/upload-pdf',                      roles: [RoleEnum.Admin]},
      ]
    },
    // Supplier Excel imports. Spreads the routes' constants, so a SubAdmin holding only the bulk
    // import permissions sees this section and nothing from Catalog.
    {
      key: 'bulkImport', title: 'sidenav.sections.bulkImport',
      items: [
        {label: 'sidenav.bulkImportJobs',   icon: 'pi pi-upload',    link: '/products/imports',          ...BULK_IMPORT_JOBS_ACCESS},
        {label: 'sidenav.bulkImportLimits', icon: 'pi pi-sliders-v', link: '/products/imports/settings', ...BULK_IMPORT_SETTINGS_ACCESS},
      ]
    },
    {
      key: 'certificates', title: 'sidenav.sections.certificates',
      items: [
        {label: 'sidenav.certificates',         icon: 'pi pi-verified',  link: '/certificates',                    roles: [RoleEnum.Admin], permissionGroup: 'certificates'},
        {label: 'sidenav.certificateTemplates', icon: 'pi pi-file-edit', link: '/project/predefined-certificates', roles: [RoleEnum.Admin], permissionGroup: 'project'},
        {label: 'sidenav.certificateCategories', icon: 'pi pi-tags',   link: '/project/certificate-categories',  roles: [RoleEnum.Admin], permissionGroup: 'project'},
        {label: 'sidenav.certificateSettings',  icon: 'pi pi-sliders-h', link: '/certificate-settings',            roles: [RoleEnum.Admin], permissionGroup: 'certificates'},
      ]
    },
    {
      key: 'finance', title: 'sidenav.sections.finance',
      items: [
        // Platform invoices (HS, SS, SC, AS) and their Odoo sync. Both spread the routes' constant.
        {label: 'sidenav.invoices',       icon: 'pi pi-file',        link: '/invoices', ...INVOICES_ACCESS},
        {label: 'sidenav.odooSync',       icon: 'pi pi-server',      link: '/odoo',     ...INVOICES_ACCESS},
        {label: 'sidenav.platformCommission', icon: 'pi pi-percentage', link: '/payment-settings/commission',    ...PAYMENT_SETTINGS_ACCESS},
        {label: 'sidenav.depositSettings',    icon: 'pi pi-sliders-h',  link: '/payment-settings/order-deposit', ...PAYMENT_SETTINGS_ACCESS},
        {label: 'sidenav.billManagement', icon: 'pi pi-receipt',     link: '/bill-management', roles: [RoleEnum.Admin], permissionGroup: 'buyers'},
        {label: 'sidenav.supplierPlans',  icon: 'pi pi-credit-card', link: '/project/subscription-plans/supplier', roles: [RoleEnum.Admin], permissionGroup: 'plans'},
        {label: 'sidenav.buyerPlans',     icon: 'pi pi-credit-card', link: '/project/subscription-plans/buyer',    roles: [RoleEnum.Admin], permissionGroup: 'plans'},
        {label: 'sidenav.ads',            icon: 'pi pi-megaphone',   link: '/ads',        roles: [RoleEnum.Admin], permissionGroup: 'ads'},
        {label: 'sidenav.settlement',     icon: 'pi pi-wallet',      link: '/settlement', roles: [RoleEnum.Admin], permissionGroup: 'settlements', comingSoon: true},
      ]
    },
    // Bank operations (/api/admin/banking). Every entry spreads the routes' constant; write actions
    // inside are gated per button on bankingOps.resolve / bankingOps.manualFund.
    {
      key: 'bankOps', title: 'sidenav.sections.bankOps',
      items: [
        {label: 'sidenav.bankOpsOverview',       icon: 'pi pi-th-large',     link: '/bank-operations/overview',             ...BANK_OPS_ACCESS},
        {label: 'sidenav.bankOpsPlatformAccounts', icon: 'pi pi-book',       link: '/bank-operations/platform-accounts',    ...BANK_OPS_ACCESS},
        {label: 'sidenav.bankOpsExceptions',     icon: 'pi pi-inbox',        link: '/bank-operations/exceptions',           ...BANK_OPS_ACCESS},
        {label: 'sidenav.bankOpsParkedMessages', icon: 'pi pi-envelope',     link: '/bank-operations/parked-messages',      ...BANK_OPS_ACCESS},
        {label: 'sidenav.bankOpsFreeze',         icon: 'pi pi-lock',         link: '/bank-operations/freeze',               ...BANK_OPS_ACCESS},
        {label: 'sidenav.bankOpsReconciliation', icon: 'pi pi-check-square', link: '/bank-operations/reconciliation',       ...BANK_OPS_ACCESS},
        {label: 'sidenav.bankOpsSweep',          icon: 'pi pi-arrow-right-arrow-left', link: '/bank-operations/sweep',      ...BANK_OPS_ACCESS},
        {label: 'sidenav.bankOpsSuppliers',      icon: 'pi pi-phone',        link: '/bank-operations/unverified-suppliers', ...BANK_OPS_ACCESS},
        {label: 'sidenav.bankOpsBankCredits',    icon: 'pi pi-plus-circle',  link: '/bank-operations/bank-credits',         ...BANK_OPS_ACCESS},
        {label: 'sidenav.bankOpsDiagnostics',    icon: 'pi pi-wrench',       link: '/bank-operations/diagnostics',          ...BANK_OPS_ACCESS},
      ]
    },
    // Nearly the whole menu of the sales roles (a Sales Manager also gets Customer Services at the top).
    {
      key: 'sales', title: 'sidenav.sections.sales',
      items: [
        {label: 'sidenav.salesManagers',    icon: 'pi pi-shield',    link: '/sales/managers',           roles: [RoleEnum.Admin]},
        {label: 'sidenav.salesTeams',       icon: 'pi pi-sitemap',   link: '/sales/teams',              roles: [RoleEnum.Admin, RoleEnum.SalesManager]},
        {label: 'sidenav.salesSalesmen',    icon: 'pi pi-users',     link: '/sales/salesmen',           roles: [RoleEnum.Admin, RoleEnum.SalesManager]},
        {label: 'sidenav.salesStarRating',  icon: 'pi pi-star',      link: '/sales/star-rating',        roles: [RoleEnum.Admin, RoleEnum.SalesManager]},
        {label: 'sidenav.salesCommissionConfig', icon: 'pi pi-cog',  link: '/sales/commission-config',  roles: [RoleEnum.Admin, RoleEnum.SalesManager]},
        {label: 'sidenav.salesKpiTargets',  icon: 'pi pi-sliders-h', link: '/sales/kpi-targets',        roles: [RoleEnum.Admin, RoleEnum.SalesManager]},
        {label: 'sidenav.salesCommission',  icon: 'pi pi-wallet',    link: '/sales/commission',         roles: [RoleEnum.Admin, RoleEnum.SalesManager]},
        {label: 'sidenav.salesReports',     icon: 'pi pi-chart-line',link: '/sales/reports',            roles: [RoleEnum.Admin, RoleEnum.SalesManager]},
        {label: 'sidenav.salesKpis',        icon: 'pi pi-gauge',     link: '/sales/kpis',               roles: [RoleEnum.Admin, RoleEnum.SalesManager, RoleEnum.CustomerService]},
        {label: 'sidenav.salesMyProfile',   icon: 'pi pi-user',      link: '/sales/my-profile',         roles: [RoleEnum.Salesman]},
        {label: 'sidenav.salesMyCommission',icon: 'pi pi-wallet',    link: '/sales/my-commission',      roles: [RoleEnum.Salesman]},
        {label: 'sidenav.salesMyReports',   icon: 'pi pi-chart-line',link: '/sales/my-reports',         roles: [RoleEnum.Salesman]},
        {label: 'sidenav.salesMyReferred',  icon: 'pi pi-users',     link: '/sales/my-referred-customers', roles: [RoleEnum.Salesman]},
        {label: 'sidenav.salesInvitationLinks', icon: 'pi pi-link',  link: '/sales/invitation-links',   roles: [RoleEnum.Salesman]},
      ]
    },
    {
      key: 'reports', title: 'sidenav.sections.reports',
      items: [
        {label: 'sidenav.reportsPerformance',       icon: 'pi pi-chart-line', link: '/reports', queryParams: {group: 'performance'},       roles: [RoleEnum.Admin], permissionGroup: 'reports'},
        {label: 'sidenav.reportsSuppliersProducts', icon: 'pi pi-truck',      link: '/reports', queryParams: {group: 'suppliersProducts'}, roles: [RoleEnum.Admin], permissionGroup: 'reports'},
        {label: 'sidenav.reportsCustomers',         icon: 'pi pi-comment',    link: '/reports', queryParams: {group: 'customers'},         roles: [RoleEnum.Admin], permissionGroup: 'reports'},
        {label: 'sidenav.reportsRequests',          icon: 'pi pi-file',       link: '/reports', queryParams: {group: 'requests'},          roles: [RoleEnum.Admin], permissionGroup: 'reports'},
        {label: 'sidenav.reportsSummaries',         icon: 'pi pi-th-large',   link: '/reports', queryParams: {group: 'summaries'},         roles: [RoleEnum.Admin], permissionGroup: 'reports'},
        {label: 'sidenav.reportsExports',           icon: 'pi pi-download',   link: '/reports', queryParams: {group: 'exports'},           roles: [RoleEnum.Admin], permissionGroup: 'reports'},
      ]
    },
    {
      key: 'ai', title: 'sidenav.sections.ai',
      items: [
        // Spread the routes' constants. Together with AI models these are the whole menu of an AI admin.
        {label: 'sidenav.aiAssistant', icon: 'pi pi-comments', link: '/ai-assistant',       ...AI_ASSISTANT_ACCESS},
        {label: 'sidenav.aiCosts',     icon: 'pi pi-dollar',   link: '/ai-cost-monitoring', ...AI_COSTS_ACCESS},
        {label: 'sidenav.aiModels',    icon: 'pi pi-microchip', link: '/project/ai-models', ...AI_MODELS_ACCESS},
      ]
    },
    {
      key: 'settings', title: 'sidenav.sections.settings',
      items: [
        {label: 'sidenav.projectSettings',    icon: 'pi pi-cog',     link: '/project/settings',              roles: [RoleEnum.Admin], permissionGroup: 'project'},
        {label: 'sidenav.registrationLookup', icon: 'pi pi-search',  link: '/suppliers/registration-lookup', roles: [RoleEnum.Admin, RoleEnum.SubAdmin], permission: 'suppliers.view'},
        {label: 'sidenav.wathqCosts',         icon: 'pi pi-receipt', link: '/wathq-costs', ...WATHQ_COSTS_ACCESS},
        {label: 'sidenav.onboardCodes',       icon: 'pi pi-reply',   link: '/landing-codes',                 roles: [RoleEnum.Admin]},
        {label: 'sidenav.auditLogs',          icon: 'pi pi-history', link: '/audit-logs',                    roles: [RoleEnum.Admin], permissionGroup: 'auditlog'},
      ]
    },
  ];

  /** Every link in the menu, across sections — what the active-link check compares against. */
  private readonly allItems: SideMenuItem[] = this.sections.flatMap(section => section.items);

  /** Keys of the titled sections the user has opened. */
  private expanded = new Set<string>(this.readExpandedSections());

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
  ) {}

  ngOnInit() {
    this.layoutService.sidenavVisible$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(visible => this.drawerVisible = visible);
  }

  /** Backdrop click / Esc / X — keep the shared state in sync. */
  onDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.layoutService.closeSidenav();
  }

  /** Any nav-link tap closes the mobile drawer (no-op on desktop). */
  onNavigate(): void {
    this.layoutService.closeSidenav();
  }

  private normalizeLink(link: string | undefined): string | null {
    if (!link) return null;
    return link.startsWith('/') ? link : '/' + link;
  }

  private urlMatches(url: string, link: string | undefined, queryParams?: Record<string, string>): boolean {
    const normalized = this.normalizeLink(link);
    if (!normalized) return false;
    const [rawPath, queryString] = url.split('#')[0].split('?');
    const pathMatches = rawPath === normalized || rawPath.startsWith(normalized + '/');
    if (!pathMatches) return false;
    if (!queryParams) return true;
    const params = new URLSearchParams(queryString ?? '');
    return Object.entries(queryParams).every(([key, value]) => params.get(key) === value);
  }

  isSectionOpen(section: SideMenuSection): boolean {
    return !section.title || this.expanded.has(section.key);
  }

  toggleSection(section: SideMenuSection) {
    if (!section.title) return;
    if (!this.expanded.delete(section.key)) this.expanded.add(section.key);
    this.saveExpandedSections();
  }

  /** The section's links this caller may open; an empty result hides the whole section. */
  visibleItems(section: SideMenuSection): SideMenuItem[] {
    return section.items.filter(item => this.canActiveSidebarItem(item));
  }

  private readExpandedSections(): string[] {
    try {
      const stored = localStorage.getItem(EXPANDED_SECTIONS_STORAGE_KEY);
      if (stored == null) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
    } catch {
      return [];
    }
  }

  private saveExpandedSections() {
    try {
      localStorage.setItem(EXPANDED_SECTIONS_STORAGE_KEY, JSON.stringify([...this.expanded]));
    } catch {
      // Storage unavailable (private mode, quota) — folding still works for this visit.
    }
  }

  /**
   * Whether another entry matches the current URL with a longer link than `normalized`
   * — i.e. names the current page more precisely.
   */
  private hasMoreSpecificMatch(normalized: string): boolean {
    return this.allItems.some(candidate => {
      const link = this.normalizeLink(candidate.link);
      return !!link
        && link.length > normalized.length
        && this.urlMatches(this.router.url, candidate.link, candidate.queryParams)
        // An entry the caller cannot see must not steal the highlight, or a page
        // reached some other way would leave nothing in the nav marked active.
        && this.canActiveSidebarItem(candidate);
    });
  }

  isActive(link: string | undefined, queryParams?: Record<string, string>): boolean {
    if (!this.urlMatches(this.router.url, link, queryParams)) return false;

    // The prefix match is what keeps Suppliers lit on /suppliers/details/:id, but it
    // also lights it on a sibling entry's own page. When another entry matches the
    // same URL more specifically, that one is the active page and this one is not.
    // urlMatches already returned false for a null link, but narrow it explicitly.
    const normalized = this.normalizeLink(link);
    if (!normalized) return false;

    return !this.hasMoreSpecificMatch(normalized);
  }

  trackByLabel(index: number, item: SideMenuItem) {
    return item.label;
  }

  trackByKey(index: number, section: SideMenuSection) {
    return section.key;
  }

  public canActiveSidebarItem(item: SideMenuItem): boolean {
    const role = this.authService.getRole();
    if (role == null) return false;

    if (role === RoleEnum.SubAdmin) {
      // SubAdmin visibility is driven by permissions only — not by item.roles.
      // A granular permission is the exact claim the endpoint checks, so it wins
      // over the coarser group, which would offer a page whose endpoint then 403s.
      if (item.permission) return this.authService.hasAdminPermission(item.permission);
      if (item.anyPermission) return this.authService.hasAnyAdminPermission(item.anyPermission);
      if (item.permissionGroup) return this.authService.hasPermissionGroup(item.permissionGroup);
      return false;
    }

    if (item.roles && !item.roles.includes(role)) return false;
    if (item.permission && !this.authService.hasAdminPermission(item.permission)) return false;
    if (item.anyPermission && !this.authService.hasAnyAdminPermission(item.anyPermission)) return false;
    return true;
  }

  protected readonly RoleEnum = RoleEnum;
}
